import type { Holder } from '../types';
import { entityIdForHolder } from './seedEntities';
import { FIDES_TENANT_ID } from './seedReference';

export const PERSONA_LOGIN_ID = 'il-bennett';
export const SECOND_LOGIN_ID = 'il-mehta';

const bennettBase = {
  tenantId: FIDES_TENANT_ID,
  contactName: 'Olivia Bennett',
  email: 'olivia.bennett@example.com',
  phone: '0412 345 678',
  address: {
    line1: '18 Wattletree Road',
    suburb: 'Malvern',
    state: 'VIC',
    postcode: '3144',
    country: 'AU',
  },
  residency: 'AU',
  fatca: 'NOT_REPORTABLE' as const,
  crs: 'NOT_REPORTABLE' as const,
  riskRating: 'LOW' as const,
  pep: false,
  communication: 'EMAIL' as const,
  adviser: 'Meridian Private Wealth',
  investorLoginId: PERSONA_LOGIN_ID,
};

export const PERSONA_HOLDERS: Holder[] = [
  {
    ...bennettBase,
    id: 'h-bennett-ind',
    investmentEntityId: entityIdForHolder('h-bennett-ind'),
    accountNumber: 'FC100001',
    name: 'Olivia Bennett',
    type: 'INDIVIDUAL',
    tfnStatus: 'QUOTED',
    tfnLast3: '482',
    kycStatus: 'VERIFIED',
    kycVerifiedAt: '2024-03-12',
    kycRefreshDue: '2027-03-12',
    wholesale: {
      basis: 'ACCOUNTANT_CERTIFICATE',
      issuedAt: '2024-11-30',
      expiresAt: '2026-11-30',
      issuer: 'Bayside Chartered Accountants',
    },
    bank: {
      bsb: '063-182',
      accountNumber: '104422871',
      accountName: 'Olivia Bennett',
      verified: true,
    },
    beneficialOwners: [],
    drpElection: true,
    createdAt: '2024-03-12',
  },
  {
    ...bennettBase,
    id: 'h-bennett-smsf',
    investmentEntityId: entityIdForHolder('h-bennett-smsf'),
    accountNumber: 'FC100002',
    name: 'Bennett Super Pty Ltd ATF Bennett Superannuation Fund',
    type: 'SMSF',
    abn: '41 552 118 903',
    acn: '622 118 903',
    tfnStatus: 'QUOTED',
    tfnLast3: '117',
    kycStatus: 'VERIFIED',
    kycVerifiedAt: '2024-03-12',
    kycRefreshDue: '2027-03-12',
    bank: {
      bsb: '063-182',
      accountNumber: '104422999',
      accountName: 'Bennett Superannuation Fund',
      verified: true,
    },
    beneficialOwners: [
      {
        id: 'bo-b1',
        name: 'Olivia Bennett',
        role: 'DIRECTOR',
        ownershipPct: 50,
        kycStatus: 'VERIFIED',
        pep: false,
        wholesale: {
          basis: 'ACCOUNTANT_CERTIFICATE',
          issuedAt: '2024-11-30',
          expiresAt: '2026-11-30',
          issuer: 'Bayside Chartered Accountants',
        },
      },
      {
        id: 'bo-b2',
        name: 'Marcus Bennett',
        role: 'DIRECTOR',
        ownershipPct: 50,
        kycStatus: 'VERIFIED',
        pep: false,
        wholesale: {
          basis: 'ACCOUNTANT_CERTIFICATE',
          issuedAt: '2025-06-15',
          expiresAt: '2027-06-15',
          issuer: 'Bayside Chartered Accountants',
        },
      },
    ],
    drpElection: false,
    createdAt: '2024-03-12',
  },
  {
    ...bennettBase,
    id: 'h-bennett-trust',
    investmentEntityId: entityIdForHolder('h-bennett-trust'),
    accountNumber: 'FC100003',
    name: 'Bennett Nominees Pty Ltd ATF Bennett Family Trust',
    type: 'TRUST',
    abn: '73 640 225 019',
    acn: '640 225 019',
    tfnStatus: 'NOT_QUOTED',
    kycStatus: 'VERIFIED',
    kycVerifiedAt: '2025-08-02',
    kycRefreshDue: '2028-08-02',
    wholesale: {
      basis: 'ACCOUNTANT_CERTIFICATE',
      issuedAt: '2025-08-01',
      expiresAt: '2027-08-01',
      issuer: 'Bayside Chartered Accountants',
    },
    bank: {
      bsb: '063-182',
      accountNumber: '104423554',
      accountName: 'Bennett Family Trust',
      verified: true,
    },
    beneficialOwners: [
      { id: 'bo-b3', name: 'Olivia Bennett', role: 'TRUSTEE', kycStatus: 'VERIFIED', pep: false },
      { id: 'bo-b4', name: 'Marcus Bennett', role: 'APPOINTOR', kycStatus: 'VERIFIED', pep: false },
    ],
    drpElection: false,
    createdAt: '2025-08-02',
  },
  {
    id: 'h-mehta-co',
    investmentEntityId: entityIdForHolder('h-mehta-co'),
    tenantId: FIDES_TENANT_ID,
    accountNumber: 'FC100004',
    name: 'Mehta Ventures Pty Ltd',
    type: 'COMPANY',
    contactName: 'Raj Mehta',
    email: 'raj.mehta@example.com',
    phone: '0433 908 112',
    address: {
      line1: '5 Ocean Street',
      suburb: 'Double Bay',
      state: 'NSW',
      postcode: '2028',
      country: 'AU',
    },
    abn: '18 604 771 230',
    acn: '604 771 230',
    tfnStatus: 'ABN_QUOTED',
    residency: 'AU',
    fatca: 'NOT_REPORTABLE',
    crs: 'NOT_REPORTABLE',
    kycStatus: 'VERIFIED',
    kycVerifiedAt: '2025-01-20',
    kycRefreshDue: '2028-01-20',
    riskRating: 'LOW',
    pep: false,
    wholesale: {
      basis: 'PROFESSIONAL_INVESTOR',
      issuedAt: '2025-01-20',
      issuer: 'Net assets over $10m (audited accounts)',
    },
    bank: {
      bsb: '032-001',
      accountNumber: '228810443',
      accountName: 'Mehta Ventures Pty Ltd',
      verified: true,
    },
    beneficialOwners: [
      {
        id: 'bo-m1',
        name: 'Raj Mehta',
        role: 'DIRECTOR',
        ownershipPct: 75,
        kycStatus: 'VERIFIED',
        pep: false,
      },
      {
        id: 'bo-m2',
        name: 'Anika Mehta',
        role: 'SHAREHOLDER',
        ownershipPct: 25,
        kycStatus: 'VERIFIED',
        pep: false,
      },
    ],
    drpElection: false,
    communication: 'EMAIL',
    createdAt: '2025-01-20',
    investorLoginId: SECOND_LOGIN_ID,
  },
];
